// ListingsCommon.js

import { Box, Button, Card, CardMedia, Chip, Typography } from '@mui/material'
import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import LikeDislikeButtons from './LikeDislikeButtons'

// ------------------------------------------------------------------------------------
// 1. StatusChips component (unchanged logic)
// ------------------------------------------------------------------------------------
const statusColors = {
  pending: 'warning',
  requested: 'info',
  accepted: 'info',
  confirmed: 'success',
  completed: 'success',
  declined: 'error',
  revoked: 'default',
  cancelled: 'default',
}

export function StatusChips({ listing, user }) {
  const { t } = useTranslation()
  const userViewing = listing.viewings?.find((v) => v?.user_id === user?.id)
  const userOffer = listing.offers?.find((o) => o?.user_id === user?.id)
  const chips = []

  if (userViewing) {
    const status = userViewing.status
    const chipColor = statusColors[status] || 'default'
    chips.push(
      <Chip
        key={`viewing-${listing.id}`}
        label={
          ['confirmed', 'accepted', 'proposed'].includes(status)
            ? `${t('listings.viewing')}: ${new Date(userViewing.viewing_date).toLocaleDateString()}`
            : `${t('listings.viewing')}: ${t(`listings.status.${status}`)}`
        }
        color={chipColor}
        variant="filled"
        sx={{ mr: 1, mb: 1 }}
      />
    )
  }

  if (userOffer) {
    if (userOffer.status === 'revoked') {
      chips.push(
        <Chip
          key={`offer-revoked-${listing.id}`}
          label={t('listings.offerRevoked')}
          color="error"
          variant="filled"
          sx={{ mr: 1, mb: 1 }}
        />
      )
    } else {
      const chipColor =
        userOffer.status === 'pending'
          ? 'info'
          : userOffer.status === 'accepted'
          ? 'success'
          : 'default'
      chips.push(
        <Chip
          key={`offer-chip-${listing.id}`}
          label={`${t('listings.offer')}: $${userOffer.offer_price} (${t(
            `listings.status.${userOffer.status}`
          )})`}
          color={chipColor}
          variant="filled"
          sx={{ mr: 1, mb: 1 }}
        />
      )
    }
  }

  // if (chips.length === 0) {
  //   chips.push(
  //     <Chip
  //       key={`none-${listing.id}`}
  //       label={t('listings.noViewingOffer')}
  //       color="default"
  //       variant="outlined"
  //       sx={{ mr: 1, mb: 1 }}
  //     />
  //   )
  // }

  return <>{chips}</>
}

// ------------------------------------------------------------------------------------
// 2. ListingCard component with improved naming and viewing datetime highlight
// ------------------------------------------------------------------------------------

export function ListingCard({ listing, user, notificationId, onLikeDislikeChange }) {
  const { t } = useTranslation()
  // Add local state to track the listing with updated like status
  const [localListing, setLocalListing] = useState(listing)

  // Update local listing when prop changes
  useEffect(() => {
    setLocalListing(listing)
  }, [listing])

  // Handle like/dislike changes locally before propagating to parent
  const handleLikeDislikeChange = useCallback(
    (listingId, newLikeValue) => {
      // Update local listing state immediately
      setLocalListing((prev) => ({
        ...prev,
        liked: newLikeValue,
      }))

      // Propagate to parent if callback exists
      if (onLikeDislikeChange) {
        onLikeDislikeChange(listingId, newLikeValue)
      }
    },
    [onLikeDislikeChange]
  )

  // Format date as relative time
  function formatRelativeDate(createdAt) {
    const date = new Date(createdAt)
    const now = new Date()

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    const diffTime = startOfToday - startOfDate
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 14) return 'Last week'
    if (diffDays < 21) return '2 weeks ago'
    if (diffDays < 28) return '3 weeks ago'
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const userViewing = listing.viewings?.find((v) => v?.user_id === user?.id)
  const userOffer = listing.offers?.find((o) => o?.user_id === user?.id)

  // Determine border color based on offer/viewing statuses
  let borderColor = '#e0e0e0'
  if (userOffer && userOffer.status === 'pending') borderColor = '#2196f3'
  if (userOffer && userOffer.status === 'revoked') borderColor = '#f44336'
  if (userViewing && userViewing.status === 'pending') borderColor = '#ffa726'
  if (userViewing && userViewing.status === 'accepted') borderColor = '#66bb6a'

  const postedDate = formatRelativeDate(listing.created_at)

  // Use the provided image_url or a fallback placeholder image.
  const imageUrl = listing.image_url || 'https://via.placeholder.com/400x300?text=No+Image'

  return (
    <Card
      component={Link}
      to={
        notificationId
          ? `/listing/${listing.id}?notificationId=${notificationId}`
          : `/listing/${listing.id}`
      }
      sx={{
        display: 'block',
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        height: '400px',
        border: `4px solid ${borderColor}`,
        transition: 'transform 0.3s ease',
        '&:hover': { transform: 'scale(1.02)' },
      }}
    >
      {/* Background image */}
      <CardMedia
        component="img"
        image={imageUrl}
        alt={listing.name}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          // Remove darkening filter for a lighter appearance
          filter: 'brightness(1)',
        }}
      />
      {/* Overlay with a lighter gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5))',
          color: '#fff',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Top Row: Title & Like/Dislike */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {localListing.name}
          </Typography>
          <LikeDislikeButtons
            listingId={localListing.id}
            onLikeDislikeChange={handleLikeDislikeChange}
          />
        </Box>

        {/* Middle: Listing details */}
        <Box>
          {listing.sold_at && (
            <Chip label={t('listings.sold', 'SOLD')} color="error" size="small" sx={{ mb: 1 }} />
          )}
          <Typography variant="body2">
            {listing.postcode}, {listing.city}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('listings.price')}: <strong>${listing.price}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('listings.postedOn')}: {postedDate}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <StatusChips listing={listing} user={user} />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}
